import { TrendingUp } from "lucide-react";

const MIN_POINTS = 7;
const WINDOW_DAYS = 30;

// Published-score snapshots inside the 30-day window (status not "insufficient"); each keeps its graded sample size.
const trendPoints = (data) => {
  const hist = data.daily_history || [];
  const cutoff = new Date(Date.now() - WINDOW_DAYS * 86400000).toISOString().slice(0, 10);
  const inWindow = hist.filter((r) => r.day >= cutoff);
  return { inWindow, pts: inWindow.filter((r) => r.market?.score != null && r.market?.status !== "insufficient") };
};

export const hasTrend = (data) => trendPoints(data).pts.length >= MIN_POINTS;

// Honest plain-language note shown in the main gauge pop-up: snapshot counts, publication requirements, no back-filling.
export const trendNote = (data) => {
  const { inWindow, pts } = trendPoints(data);
  const m = data.current.market, need = m.needed || {};
  if (pts.length >= MIN_POINTS) return `${pts.length} daily snapshots with a published score in the last 30 days (needs ${MIN_POINTS}); the trend below the gauge is drawn only from those real points. Nothing is back-filled or estimated.`;
  return `Not enough history yet. ${inWindow.length} daily snapshot${inWindow.length === 1 ? "" : "s"} in the last 30 days, ${pts.length} with a published score (needs ${MIN_POINTS}).${m.status === "insufficient" ? ` A score is only published once ${m.resolved + (need.resolved || 0)} graded and ${(m.directional?.n || 0) + (need.directional || 0)} directional predictions exist (now ${m.resolved} / ${m.directional?.n || 0}).` : ""} Nothing is back-filled or estimated.`;
};

// 30-day trend of the *published* Prediction Accuracy Score. Renders only with ≥ MIN_POINTS genuine points; otherwise nothing
// (the explanation lives in the main gauge pop-up via trendNote).
export const ScoreTrend = ({ data }) => {
  const { pts } = trendPoints(data);
  if (pts.length < MIN_POINTS) return null;
  const w = 320, h = 64, pad = 6;
  const min = Math.min(...pts.map((p) => p.market.score)), max = Math.max(...pts.map((p) => p.market.score));
  const sx = (i) => pad + (i / (pts.length - 1)) * (w - 2 * pad);
  const sy = (v) => h - pad - ((v - (min === max ? min - 1 : min)) / ((max - min) || 2)) * (h - 2 * pad);
  const first = pts[0], last = pts[pts.length - 1], delta = Math.round((last.market.score - first.market.score) * 10) / 10;
  return (
    <div className="w-full max-w-[340px]" data-testid="gauge-trend">
      <div className="flex items-center justify-between text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400">
        <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-cyan-300" /> 30-day score trend</span>
        <span data-testid="gauge-trend-delta" className={delta > 0 ? "text-emerald-300" : delta < 0 ? "text-red-300" : "text-slate-400"}>{delta > 0 ? "+" : ""}{delta} pts</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" data-testid="gauge-trend-svg">
        <polyline fill="none" stroke="#22d3ee" strokeWidth="2" points={pts.map((p, i) => `${sx(i)},${sy(p.market.score)}`).join(" ")} />
        {pts.map((p, i) => <circle key={p.day} cx={sx(i)} cy={sy(p.market.score)} r="2.5" fill="#22d3ee"><title>{p.day}: score {p.market.score} · {p.market.resolved} graded</title></circle>)}
      </svg>
      <div className="flex justify-between text-[10px] font-mono-ticker text-slate-500" data-testid="gauge-trend-range">
        <span>{first.day} · {first.market.score} ({first.market.resolved} graded)</span><span>{last.day} · {last.market.score} ({last.market.resolved} graded)</span>
      </div>
    </div>
  );
};
